import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import HeroHeading from "Components/HeroHeading";
import polishing from "Assets/Image/News/news-publish.png";
import styles from "./news.module.scss";
import { newsPosts } from "../../data/newsPosts";

const NewsDetails = () => {
  const { slug } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Find the news post by slug
    const foundNews = Object.values(newsPosts).find(post => post.slug === slug);
    setNews(foundNews || null);
    setLoading(false);
  }, [slug]);

  // Handle meta tags using useEffect
  useEffect(() => {
    // Only set meta tags when news data is available
    if (news) {
      // Set document title
      document.title = news.title;
      
      // Create or update meta tags
      const metaTags = [
        {
          name: "description",
          content: news.metaDescription || news.subtitle
        },
        {
          property: "og:title",
          content: news.title
        },
        {
          property: "og:description",
          content: news.metaDescription || news.subtitle
        },
        {
          property: "og:url",
          content: `https://www.arjivexports.com/news/${slug}`
        },
        {
          property: "og:type",
          content: "article"
        },
        {
          property: "og:image",
          content: `https://arjivexports.com${news.image}`
        }
      ];

      // Set canonical link
      let canonicalLink = document.querySelector("link[rel='canonical']");
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.rel = "canonical";
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = `https://www.arjivexports.com/news/${slug}`;

      // Add or update meta tags
      metaTags.forEach(metaInfo => {
        let metaTag = document.querySelector(`meta[${metaInfo.name ? 'name' : 'property'}="${metaInfo.name || metaInfo.property}"]`);
        if (!metaTag) {
          metaTag = document.createElement("meta");
          metaTag.setAttribute(metaInfo.name ? 'name' : 'property', metaInfo.name || metaInfo.property);
          document.head.appendChild(metaTag);
        }
        metaTag.setAttribute("content", metaInfo.content);
      });

      // Add structured data for news post
      let scriptTag = document.querySelector("script[type='application/ld+json']");
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.type = "application/ld+json";
        document.head.appendChild(scriptTag);
      }
      
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "NewsPosting",
        "headline": news.title,
        "description": news.metaDescription || news.subtitle,
        "image": `https://arjivexports.com${news.image}`,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://arjivexports.com/news/${slug}`
        }
      };
      
      scriptTag.textContent = JSON.stringify(structuredData);
    }

    // Clean up function
    return () => {
      // Optional: Remove structured data script when component unmounts
      const scriptTag = document.querySelector("script[type='application/ld+json']");
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, [news, slug]);

  if (loading) {
    return <div className="container mx-auto px-4 py-12">Loading...</div>;
  }

  if (!news) {
    return <Navigate to="/news" replace />;
  }

  return (
    <main className={styles.news}>
      <HeroHeading img={polishing} title={news.publishTitle} />
      <div className="max-w-3xl mx-auto px-4 py-12 bg-white">
        {/* Header with navigation */}
        <div className="mb-8">
          <Link 
            to="/news" 
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
            <span>Back to News</span>
          </Link>
        </div>
        
        {/* News Title */}
        <h1 className="text-4xl font-serif text-amber-900 mb-6">
          {news.title}
        </h1>

        {/* Featured Image - Responsive on all screen sizes */}
        <div className="mb-8 w-full">
          <div className="relative w-full h-0 pb-[56.25%] overflow-hidden rounded-lg shadow-md">
            <img 
              src={news.image} 
              alt={news.alt}
              className="absolute top-0 left-0 w-full h-full object-cover"
              loading="eager"
            />
          </div>
          {news.imageCaption && (
            <p className="text-sm text-gray-600 mt-2 text-center italic">
              {news.imageCaption}
            </p>
          )}
        </div>
        
        {/* News Subtitle */}
        <div className="border-l-4 border-gray-300 pl-4 mb-8">
          <p className="text-gray-700 italic">
            {news.subtitle}
          </p>
        </div>
        <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: news.content || <></> }}>
        </div>
        
        {/* Bottom navigation */}
        <div className="mt-12 pt-6 border-t border-gray-200">
          <Link 
            to="/news" 
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
            <span>Return to News</span>
          </Link>
        </div>
      </div>
    </main>
  );
};

export default NewsDetails;